import { create } from 'zustand';
import { WorkloadType } from '../types';

const { DEPLOYMENT, STATEFULSET, DAEMONSET } = WorkloadType;

/** 枚举复杂项目更好维护 */
export enum UpdateStrategyValues {
  /** 更新策略：滚动更新 */
  ROLLING_UPDATE = 'RollingUpdate',
  /** 更新策略：同时 */
  RECREATE = 'Recreate',
  /** statefulset: 删除时候更新 */
  ON_DELETE = 'OnDelete',
}

const { ROLLING_UPDATE } = UpdateStrategyValues;

interface UpdateStrategyState {
  updateStrategy: UpdateStrategyValues;
  /** 只有 RollingUpdate 才有下面这两个参数 */
  maxUnavailablePods?: string;
  maxExtraPods?: string;
  /** 当且仅当 statefulset 的时候 */
  partition?: number;
  /** 只有 DaemonSet 才有这个参数 */
  minReadySeconds?: number;
}

const initialDeployment: UpdateStrategyState = {
  updateStrategy: ROLLING_UPDATE,
  maxUnavailablePods: '25%',
  maxExtraPods: '25%',
};

const initialStatefulset: UpdateStrategyState = {
  updateStrategy: ROLLING_UPDATE,
  partition: 0,
};

const initialDaemonset: UpdateStrategyState = {
  updateStrategy: ROLLING_UPDATE,
  maxUnavailablePods: '20%',
  minReadySeconds: 0,
};

interface UpdateStrategyAction {
  setUpdateStrategy: (module: WorkloadType, strategy: UpdateStrategyValues) => void;
  updateMaxUnavailablePods: (pods: string) => void;
  updateMaxExtraPods: (pods: string) => void;
  updatePartition: (partition: number) => void;
  updateMinReadySeconds: (seconds: number) => void;
  reset: (module: WorkloadType) => void;
}

export const useUpdateStrategyStore = create<UpdateStrategyState & UpdateStrategyAction>()(
  (set) => ({
    ...initialDeployment,
    setUpdateStrategy: (module, strategy) => {
      switch (module) {
        case DAEMONSET: {
          if (strategy === ROLLING_UPDATE) {
            set((state) => ({
              updateStrategy: strategy,
              maxUnavailablePods: state.maxUnavailablePods || '20%',
              minReadySeconds: state.minReadySeconds || 0,
            }));
          } else {
            set({
              updateStrategy: strategy,
              maxUnavailablePods: undefined,
              minReadySeconds: undefined,
            });
          }
          break;
        }
        case STATEFULSET:
          if (strategy === ROLLING_UPDATE) {
            set({ updateStrategy: strategy, partition: 0 });
          } else {
            set({ updateStrategy: strategy });
          }
          break;
        case DEPLOYMENT:
        default: {
          if (strategy === ROLLING_UPDATE) {
            set((state) => ({
              updateStrategy: strategy,
              maxUnavailablePods: state.maxUnavailablePods || '25%',
              maxExtraPods: state.maxExtraPods || '25%',
            }));
          } else {
            // 非滚动更新模式，清除相关参数
            set({
              updateStrategy: strategy,
              maxUnavailablePods: undefined,
              maxExtraPods: undefined,
            });
          }
          break;
        }
      }
    },
    updateMaxUnavailablePods: (pods) => set({ maxUnavailablePods: pods }),
    updateMaxExtraPods: (pods) => set({ maxExtraPods: pods }),
    updatePartition: (partition) => set({ partition }),
    updateMinReadySeconds: (seconds) => set({ minReadySeconds: seconds }),
    reset: (module) => {
      switch (module) {
        case DAEMONSET:
          set({ ...initialDaemonset });
          break;
        case STATEFULSET:
          set({ ...initialStatefulset });
          break;
        case DEPLOYMENT:
        default:
          set({ ...initialDeployment });
          break;
      }
    },
  }),
);
